import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { money, moneyK, fecha, Title, Badge, Empty, Kpi } from './ui';

export const ESTADO_PROPUESTA = {
  borrador: { label: 'Borrador', color: '#64748b', bg: 'rgba(100,116,139,0.15)' },
  a_preparar: { label: 'A preparar', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  a_ofrecer: { label: 'A ofrecer', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  enviada: { label: 'Enviada', color: '#a855f7', bg: 'rgba(168,85,247,0.15)' },
  vista: { label: 'La vio', color: '#0ea5e9', bg: 'rgba(14,165,233,0.15)' },
  confirmada: { label: 'Aceptada', color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
  no_cerro: { label: 'No cerró', color: '#ef4444', bg: 'rgba(239,68,68,0.15)' },
};

export default function AgenciaPropuestas() {
  const [rows, setRows] = useState(null);
  const [nueva, setNueva] = useState(null);
  const [clientes, setClientes] = useState([]);
  const navigate = useNavigate();

  const cargar = () => api.get('/agency/propuestas').then(r => setRows(r.data)).catch(() => toast.error('No se pudo cargar'));
  useEffect(() => { cargar(); api.get('/agency/clientes').then(r => setClientes(r.data)).catch(() => {}); }, []);

  const crear = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/agency/propuestas', { ...nueva, estado: 'borrador' });
      navigate('/agencia/propuestas/' + data.id);
    } catch { toast.error('No se pudo crear'); }
  };

  if (!rows) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '8px' };
  const td = { padding: '10px 8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const abiertas = rows.filter(r => ['enviada', 'vista'].includes(r.estado));
  const ganadas = rows.filter(r => r.estado === 'confirmada');
  const cerradas = rows.filter(r => ['confirmada', 'no_cerro'].includes(r.estado));

  return (
    <div>
      <Title right={<button className="btn btn-primary" onClick={() => setNueva({ titulo: '', cliente_id: '', vigencia_dias: 30 })}><Plus size={16} /> Nueva propuesta</button>}>
        Propuestas
      </Title>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi label="Esperando respuesta" value={abiertas.length} sub={moneyK(abiertas.reduce((a, r) => a + Number(r.monto || 0), 0))} color="#0ea5e9" />
        <Kpi label="Aceptadas" value={ganadas.length} sub={moneyK(ganadas.reduce((a, r) => a + Number(r.monto || 0), 0))} color="#22c55e" />
        <Kpi label="Efectividad" value={cerradas.length ? Math.round((ganadas.length / cerradas.length) * 100) + '%' : '—'} sub={`${cerradas.length} cerradas`} />
        <Kpi label="Total" value={rows.length} />
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-container">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>Propuesta</th><th style={th}>Cliente</th><th style={{ ...th, textAlign: 'right' }}>Monto</th>
              <th style={th}>Estado</th><th style={th}>Enviada</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={6} style={td}><Empty>Todavía no cargaste ninguna propuesta</Empty></td></tr>
                : rows.map(p => {
                  const e = ESTADO_PROPUESTA[p.estado] || {};
                  return (
                    <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/agencia/propuestas/' + p.id)}>
                      <td style={{ ...td, fontWeight: 600 }}>{p.titulo}</td>
                      <td style={td}>{p.cliente || <span style={{ color: 'var(--text-muted)' }}>Sin cliente</span>}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{p.monto ? money(p.monto) : '—'}</td>
                      <td style={td}><Badge label={e.label} color={e.color} bg={e.bg} />
                        {p.vista_at && p.estado !== 'confirmada' && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>vista {fecha(p.vista_at)}</div>}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-muted)', fontSize: 12 }}>{p.enviada_at ? fecha(p.enviada_at) : '—'}</td>
                      <td style={td}>{p.link && p.estado !== 'borrador' && (
                        <a href={p.link} target="_blank" rel="noopener noreferrer" onClick={ev => ev.stopPropagation()} title="Ver como la ve el cliente"><ExternalLink size={15} /></a>
                      )}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {nueva && (
        <div className="modal-overlay" onClick={() => setNueva(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <h2>Nueva propuesta</h2>
            <form onSubmit={crear}>
              <div className="form-group"><label>Título</label>
                <input value={nueva.titulo} onChange={e => setNueva({ ...nueva, titulo: e.target.value })} placeholder="Sitio web institucional" required autoFocus /></div>
              <div className="form-group"><label>Cliente</label>
                <select value={nueva.cliente_id} onChange={e => setNueva({ ...nueva, cliente_id: e.target.value })}>
                  <option value="">Todavía no lo sé</option>
                  {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Si es un cliente nuevo, cargalo primero en Clientes.</div>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setNueva(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Crear y redactar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
